import { useEffect } from "react"
import AemGrid from "@/components/AemGrid"
import BreadCrumb from "@/components/Default/BreadCrumb"
import GlobalPageFooter from "@/components/Shared/GlobalPage/GlobalPageFooter"
import GlobalPageHeader from "@/components/Shared/GlobalPage/GlobalPageHeader"
import GlobalPageLayout from "@/components/Shared/GlobalPage/GlobalPageLayout"
import {
  footerUrl,
  headerUrl,
  previewContentPrefix,
} from "@/constants"
import { aemAxiosInstance, aemPreviewAxiosInstance } from "@/constants/api"
import {
  filterBackEndJson,
  getAnalyticsScript,
  getClienIP,
  getDataLayerObj,
  removeAndCleanLink,
} from "@/utils/helper"
import { serverSideTranslations } from "next-i18next/serverSideTranslations"
import { useDispatch } from "react-redux"
import {
  setDatalayerPageName,
  setGbhDataLayerCustom,
} from "@/store/features/genericSlice"

const AsProductList = ({
  tmpData: data,
  headertmp: header,
  footertmp: footer,
  ip,
  adobeAnalytic,
  globalScript,
  footerScript,
  analyticsData,
  datalayerPageName,
  gbhDataLayerCustom,
}) => {
  const dispatch = useDispatch()
  let breadCrumb = [];
  extractBreadCrumb(data)
  useEffect(() => {
    if (ip) sessionStorage.setItem("true-ip", ip)
  }, [ip])
  useEffect(() => {
    getAnalyticsScript(adobeAnalytic)
  }, [adobeAnalytic])
  useEffect(() => {
    getDataLayerObj(analyticsData)
  }, [analyticsData])
  useEffect(() => {
    dispatch(setDatalayerPageName(datalayerPageName))
    dispatch(setGbhDataLayerCustom(gbhDataLayerCustom))

    extractBreadCrumb(data)
    return () => {
      dispatch(setDatalayerPageName(""))
      dispatch(setGbhDataLayerCustom(""))
    }
  }, [datalayerPageName, gbhDataLayerCustom])

    // Recursive extractor
  function extractBreadCrumb(data) {
    if (Array.isArray(data)) {
      data.forEach(item => extractBreadCrumb(item, results));
    } else if (typeof data === "object" && data !== null) {
      if (data[":type"] === "kf/components/breadcrumb/v1/breadcrumb" && Array.isArray(data["items"])) {
         breadCrumb = data["items"]
         return
      }else{
          const itemsOrder = data[":itemsOrder"]
          return itemsOrder?.map((itemOrder, idx) => {
            const items = data[":items"][itemOrder]
            if(breadCrumb.length === 0)
              extractBreadCrumb(items)
            else
              return
          })
      }
    }
  }

  return (
    <GlobalPageLayout
      header={
        <GlobalPageHeader
          header={header}
          title={data.title}
          globalScripting={globalScript}
          breadCrumb={breadCrumb}
        />
      }
      footer={<GlobalPageFooter footer={footer} footerScripting={footerScript} />}
    >
      <div className="min-vh-100" style={{ background: "transparent" }}>
        <AemGrid
          data={data}
          count={0}
          breadCrumb={(data, fullData) => (
            <BreadCrumb data={data} fullData={fullData} />
          )}
        />
      </div>
    </GlobalPageLayout>
  )
}
/**
 * Get ServerSideProps together.
 * @param {Object} request The first number.
 * @return {object} The props.
 */
export async function getServerSideProps({ req, query, locale }) {
  const isPreviewMode = req.cookies["aem-author-preview-mode"]
  const ip = getClienIP(req)
  const { material = "" } = query ?? {}
  const axiosInstance = isPreviewMode ? aemPreviewAxiosInstance : aemAxiosInstance
  const getUrl = () => {
    if (req?.url?.includes("/p/")) {
      return `${previewContentPrefix}/p/${material}.model.json`
    }
    if (req?.url?.includes("/savoy-products")) {
      return `${previewContentPrefix}/${material}/savoy-products.model.json`
    }
    return `${previewContentPrefix}/${material}/products.model.json`
  }
  const newDataFetchUrl = getUrl();
  console.log("Model json we are trying to access -> ", newDataFetchUrl);

  const { data } = await axiosInstance({
    url: newDataFetchUrl,
    method: "GET",
  })

    // code to add redirect logic
  const { redirectTarget = {}, pageData = {}, redirectUrl } = data || {}

if ((pageData?.redirect && pageData?.redirect !== "n/a") || (redirectUrl && redirectUrl !== "n/a")) {
    let link = (redirectUrl && redirectUrl !== "n/a") ? 
                  removeAndCleanLink(redirectUrl).trim() || "/" :        
                  removeAndCleanLink(pageData?.redirect).trim() || "/"
    return {
      redirect: {
        destination: link,
        permanent: false,
      },
    }
  } else if (redirectTarget?.page?.path) {
    return {
      redirect: {
        destination: removeAndCleanLink(redirectTarget.page.path),
        permanent: false,
      },
    }
  }
  const fetchData = async (url) =>
    isPreviewMode
      ? await aemPreviewAxiosInstance({ url, method: "GET" })
      : await aemAxiosInstance({ url, method: "GET" })

  const { data: header } = await fetchData(
    `${data.headerExpFrg ? data.headerExpFrg : headerUrl}.model.json`
  )

  const { data: footer } = await fetchData(
    `${data.footerExpFrg ? data.footerExpFrg : footerUrl}.model.json`
  )

  const globalScript = data?.globalScript || ""
  const footerScript = data?.footerScript || ""

  const analyticsScriptClass = data?.analyticsScriptClass
    ? data?.analyticsScriptClass
    : null
  const gbhDataLayerCustom = {}
  if (data?.datalayerPageName) {
    gbhDataLayerCustom.datalayerPageName = data?.datalayerPageName
  }
  if (data?.eventPageType) {
    gbhDataLayerCustom.eventPageType = data?.eventPageType
  }
  if (data?.pageType) {
    gbhDataLayerCustom.pageType = data?.pageType
  }
  if (
    !data?.pageComponentProperties?.description &&
    data?.pageComponentProperties?.dataLayer &&
    data?.pageComponentProperties?.dataLayer[data?.pageComponentProperties?.id]
  ) {
    gbhDataLayerCustom.dataLayer =
      data?.pageComponentProperties?.dataLayer[
        data?.pageComponentProperties?.id
      ]
  }
  const analyticsData = data?.data
  let tmpData = {}
  const adobeAnalytic = data?.adobeAnalytic || null
  if (data.pageComponentProperties) {
    tmpData = data.pageComponentProperties
  } else {
    tmpData = data
  }
  const headertmp = filterBackEndJson(header)
  const footertmp = filterBackEndJson(footer)
  const datalayerPageName = data?.datalayerPageName
  return {
    props: {
      ip,
      tmpData,
      headertmp,
      footertmp,
      adobeAnalytic,
      globalScript: globalScript.includes("<") ? globalScript : "",
      footerScript: footerScript.includes("<") ? footerScript : "",
      analyticsData,
      analyticsScriptClass,
      datalayerPageName,
      gbhDataLayerCustom,
      ...(await serverSideTranslations(locale, ["common"])),
    },
  }
}
export default AsProductList
